// Encode captured audio as a 16 kHz, 16-bit mono WAV (what the recognisers expect).

function concat(frames: Float32Array[]): Float32Array {
  const total = frames.reduce((n, f) => n + f.length, 0);
  const out = new Float32Array(total);
  let o = 0;
  for (const f of frames) {
    out.set(f, o);
    o += f.length;
  }
  return out;
}

/** Simple low-pass + linear interpolation (fallback when OfflineAudioContext is unavailable). */
function resampleJs(input: Float32Array, from: number, to: number): Float32Array {
  if (from === to) return input;
  const ratio = from / to;
  // box filter over one output period removes most aliasing for speech
  const win = Math.max(1, Math.round(ratio));
  const smooth = new Float32Array(input.length);
  let acc = 0;
  for (let i = 0; i < input.length; i++) {
    acc += input[i];
    if (i >= win) acc -= input[i - win];
    smooth[i] = acc / Math.min(win, i + 1);
  }
  const len = Math.floor(input.length / ratio);
  const out = new Float32Array(len);
  for (let i = 0; i < len; i++) {
    const x = i * ratio;
    const j = Math.floor(x);
    const k = Math.min(j + 1, smooth.length - 1);
    out[i] = smooth[j] + (smooth[k] - smooth[j]) * (x - j);
  }
  return out;
}

async function resample(input: Float32Array, from: number, to: number): Promise<Float32Array> {
  if (from === to) return input;
  const OAC = window.OfflineAudioContext || (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  if (OAC) {
    try {
      const len = Math.max(1, Math.ceil((input.length * to) / from));
      const oc = new OAC(1, len, to);
      const buf = oc.createBuffer(1, input.length, from);
      buf.copyToChannel(input as Float32Array<ArrayBuffer>, 0);
      const src = oc.createBufferSource();
      src.buffer = buf;
      src.connect(oc.destination);
      src.start();
      const out = await oc.startRendering();
      return out.getChannelData(0).slice();
    } catch {
      /* fall through */
    }
  }
  return resampleJs(input, from, to);
}

export function pcmToWav(samples: Float32Array, rate: number): Blob {
  const data = new DataView(new ArrayBuffer(44 + samples.length * 2));
  const w = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) data.setUint8(o + i, s.charCodeAt(i));
  };
  w(0, "RIFF");
  data.setUint32(4, 36 + samples.length * 2, true);
  w(8, "WAVE");
  w(12, "fmt ");
  data.setUint32(16, 16, true);
  data.setUint16(20, 1, true);
  data.setUint16(22, 1, true);
  data.setUint32(24, rate, true);
  data.setUint32(28, rate * 2, true);
  data.setUint16(32, 2, true);
  data.setUint16(34, 16, true);
  w(36, "data");
  data.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    data.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([data.buffer], { type: "audio/wav" });
}

export async function encodeWav16k(frames: Float32Array[], sampleRate: number): Promise<Blob> {
  const pcm = await resample(concat(frames), sampleRate, 16000);
  return pcmToWav(pcm, 16000);
}
